import { SceneBase } from './SceneBase';

export class SpinePreviewer extends SceneBase {
  constructor() {
    super({ key: 'SpinePreviewer', active: false });
  }

  init(config) {
    this._config = config.data;
  }

  preload() {
    // load spinner
    this.spinner = this.add.graphics();
    for (let i = 0; i < 360; i += 40) {
      const tx = 50 * Math.sin(i * (Math.PI / 180));
      const ty = 50 * Math.cos(i * (Math.PI / 180));
      this.spinner.fillStyle(0xffffff, 1 - i / 360);
      this.spinner.fillCircle(tx, ty, 10);
    }
    this.spinner.x = 840;
    this.spinner.y = 360;
    this.tweens.add({
      targets: this.spinner,
      angle: 360,
      repeat: -1,
      duration: 1000,
    });
  }

  // eslint-disable-next-line max-statements
  create() {
    super.create();

    // hide loader
    this.spinner.visible = false;

    // grab animations
    this.config = this.game.previewerData;
    if (!this.config) return;

    this.defaultBackgroundColour = this.config.background || 0x000000;
    this.defaultPosition = this.config.screen_position || { x: 840, y: 520 };
    // set backround colour
    this.cameras.main.setBackgroundColor(this.defaultBackgroundColour);

    // create options container
    this.domContainer = document.createElement('div');
    this.domContainer.className = 'spine_options';
    this.domContainer.style.position = 'absolute';
    this.domContainer.style.top = '10px';
    this.domContainer.style.right = '10px';
    this.game.scale.parent.appendChild(this.domContainer);

    let title = document.createElement('p');
    title.innerHTML = 'Spine';
    this.domContainer.appendChild(title);

    // create spine select
    this.spineoptions = document.createElement('select');
    this.config.spines.forEach((entry, index) => {
      const option = document.createElement('option');
      if (typeof entry === 'string') {
        const parts = entry.split('/');
        option.innerHTML = entry.display_name || parts[parts.length - 1].replace(/_/g, ' ');
        option.value = index;
      } else if (entry.divider) {
        option.innerHTML = entry.divider;
        option.disabled = true;
      } else if (entry.path) {
        const parts = entry.path.split('/');
        option.innerHTML = entry.display_name || parts[parts.length - 1].replace(/_/g, ' ');
        option.value = index;
      } else {
        option.innerHTML = '-';
        option.disabled = true;
      }
      this.spineoptions.appendChild(option);
    });
    this.spineoptions.addEventListener('change', () => {
      this.handleSpineSelect(this.config.spines[this.spineoptions.value]);
    });
    this.domContainer.appendChild(this.spineoptions);

    title = document.createElement('p');
    title.innerHTML = 'Animation';
    this.domContainer.appendChild(title);

    // animaton select
    this.animationOptions = document.createElement('select');
    this.animationOptions.label = title;
    this.animationOptions.addEventListener('change', () => {
      this.handleAnimationSelect(this.activeSpine.animations[this.animationOptions.value]);
    });
    this.domContainer.appendChild(this.animationOptions);

    title = document.createElement('p');
    title.innerHTML = 'Skin';
    this.domContainer.appendChild(title);

    // animaton select
    this.skinOptions = document.createElement('select');
    this.skinOptions.label = title;
    this.skinOptions.addEventListener('change', () => {
      this.handleSkinSelect(this.activeSpine.skins[this.skinOptions.value]);
    });
    this.domContainer.appendChild(this.skinOptions);

    // load first spine
    this.handleSpineSelect(this.config.spines[this.spineoptions.value]);
  }

  /*
  =========================================================================================================
    SPINE MANAGEMENT
  =========================================================================================================
  */

  handleSpineSelect(spineconfig) {
    // destroy active spine
    if (this.loadedSpine) {
      this.loadedSpine.destroy();
      this.loadedSpine = null;
    }

    // disable drop downs
    this.spineoptions.setAttribute('disabled', true);
    this.animationOptions.setAttribute('disabled', true);

    if (typeof spineconfig === 'string') {
      this.activeSpine = {
        path: spineconfig,
      };
    } else {
      this.activeSpine = spineconfig;
    }
    if (!this.activeSpine.key) {
      const parts = this.activeSpine.path.split('/');
      this.activeSpine.key = parts[parts.length - 1];
    }

    const dirparts = this.activeSpine.path.split('/');
    dirparts.pop();

    // load new spine
    this.load.setPath(this.game.appUrls.getAssetsDirectory(dirparts.join('/')));
    this.load.spine(
      this.activeSpine.key,
      this.activeSpine.key + '.json',
      this.activeSpine.key + '.atlas'
    );
    this.load.once('complete', this.handleSpineLoaded, this);
    this.load.start();

    // show loader
    this.spinner.visible = true;
  }

  handleSpineLoaded() {
    // hide loader
    this.spinner.visible = false;

    // add spine
    this.loadedSpine = this.add.spine(
      this.defaultPosition.x,
      this.defaultPosition.y,
      this.activeSpine.key
    );
    // set transform overrides
    const transform = this.activeSpine.transform || {};
    this.spineDefaultTransform = {
      x: this.defaultPosition.x + transform.x || this.defaultPosition.x,
      y: this.defaultPosition.y + transform.y || this.defaultPosition.y,
      scale: transform.scale || 1,
    };
    // apply default transforms
    this.setSpineTransforms(this.activeSpine.transform);
    // set background colour if applicable
    this.cameras.main.setBackgroundColor(this.activeSpine.background || this.config.background);
    // set animations
    this.renderAnimationOptions();
    // enable spine dropdown
    this.spineoptions.removeAttribute('disabled');
  }

  setSpineTransforms(overrides = {}) {
    this.loadedSpine.x = this.defaultPosition.x + overrides.x || this.spineDefaultTransform.x;
    this.loadedSpine.y = this.defaultPosition.y + overrides.y || this.spineDefaultTransform.y;
    this.loadedSpine.setScale(overrides.scale || this.spineDefaultTransform.scale);
  }

  /*
  =========================================================================================================
    ANIMATON MANAGEMENT
  =========================================================================================================
  */

  renderAnimationOptions() {
    // flush current opions from select menu
    this.animationOptions.innerHTML = '';
    this.skinOptions.innerHTML = '';

    // get animation list
    const anims = this.loadedSpine.getAnimationList();
    this.activeSpine.animations = anims.reduce((arr, anim) => {
      if (this.activeSpine.exclude && this.activeSpine.exclude.includes(anim)) {
        return arr;
      }
      if (this.activeSpine.include && !this.activeSpine.include.includes(anim)) {
        return arr;
      }
      const overrides = this.activeSpine.animationoverride
        ? this.activeSpine.animationoverride[anim] || {}
        : {};
      arr.push({ display_name: overrides.name || anim.replace(/_/g, ' '), spine_name: anim });
      return arr;
    }, []);

    let activeAnim = 0;
    // loop through each option, and add to select menu
    this.activeSpine.animations.forEach((entry, index) => {
      const option = document.createElement('option');
      option.innerHTML = entry.display_name || entry.spine_name.replace(/_/g, ' ');
      option.value = index;
      // if (this.activeAnimation && entry.spine_name === this.activeAnimation.spine_name) {
      //   activeAnim = index;
      // }
      this.animationOptions.appendChild(option);
    });
    // enable dropdown selector
    this.animationOptions.removeAttribute('disabled');
    this.animationOptions.value = activeAnim;
    this.animationOptions.style.visibility = 'visible';
    this.animationOptions.label.style.visibility = 'visible';
    // start initial animation
    this.handleAnimationSelect(this.activeSpine.animations[activeAnim]);

    // check skins exist
    this.activeSpine.skins = [];
    this.loadedSpine.getSkinList().forEach((skin) => {
      this.loadedSpine.setSkinByName(skin);
      const bounds = this.loadedSpine.getBounds();
      if (bounds.offset.x !== Infinity && bounds.offset.y !== Infinity) {
        this.activeSpine.skins.push(skin);
      }
    });

    let activeSkin = 0;
    // loop through each option, and add to select menu
    this.activeSpine.skins.forEach((entry, index) => {
      const option = document.createElement('option');
      option.innerHTML = entry;
      option.value = index;
      // if (this.activeSkin && entry === this.activeSkin) {
      //   activeSkin = index;
      // }
      this.skinOptions.appendChild(option);
    });
    // enable dropdown selector
    this.skinOptions.removeAttribute('disabled');
    this.skinOptions.value = activeSkin;
    this.skinOptions.style.visibility = 'visible';
    this.skinOptions.label.style.visibility = 'visible';
    // start initial skin
    this.handleSkinSelect(this.activeSpine.skins[activeSkin]);
  }

  handleAnimationSelect(animation) {
    // clear active listeners
    this.loadedSpine.removeAllListeners('complete');
    // check if animation is a chain of animations
    const chain = animation.spine_name.split(',');
    // if chain, trigger loop through anims
    if (chain.length > 1) {
      this.chainAnimation = true;
      this.loadedSpine.chainIndex = 0;
      this.loadedSpine.on('complete', () => {
        this.spine.chainIndex++;
        this.loadedSpine.play(chain[this.loadedSpine.chainIndex % chain.length]);
      });
      this.loadedSpine.play(chain[this.loadedSpine.chainIndex % chain.length]);
    } else {
      // otherwise we just loop animation
      this.loadedSpine.play(animation.spine_name, true, false);
    }
    // log animation
    this.activeAnimation = animation;
    // update transforms
    this.setSpineTransforms(animation.transform);
    // console.log(this.activeSpine.animationoverride);
    if (
      this.activeSpine.animationoverride &&
      this.activeSpine.animationoverride[animation.spine_name]
    ) {
      // console.log(this.activeSpine.animationoverride[animation.spine_name]);
      const overrides = this.activeSpine.animationoverride[animation.spine_name];
      this.setSpineTransforms(overrides.transform);
    }
  }

  handleSkinSelect(skin) {
    this.loadedSpine.setSkinByName(skin);
    this.loadedSpine.setToSetupPose();
    this.activeSkin = skin;
    this.setSpineTransforms();
  }

  /*
  =========================================================================================================
    SHUTDOWN
  =========================================================================================================
  */

  shutdown() {
    super.shutdown();
  }
}
